/**
 * Состояние ленты одной переписки и правила его обновления.
 *
 * Лента — массив по возрастанию времени. Свои неотправленные сообщения лежат в ней же
 * с пометкой local (оптимистичная отправка) и склеиваются с ответом сервера по echo_id:
 * сервер возвращает его и в ответе на отправку, и в событии message.created, и приходит
 * то одно, то другое первым — поэтому склейка обязана быть идемпотентной.
 */
import { MESSAGE_OUTGOING, type CwAttachment, type CwMessage, type UiMessage } from '@/api/inboxTypes';

export interface MessagesState {
  messages: UiMessage[];
  /** Есть ли что-то старше самого раннего загруженного. */
  hasMore: boolean;
}

/** Размер страницы сообщений Chatwoot (message_finder.rb: limit 20). */
export const MESSAGES_PAGE = 20;

export const emptyState = (): MessagesState => ({ messages: [], hasMore: false });

function cmp(a: UiMessage, b: UiMessage): number {
  if (a.created_at !== b.created_at) return a.created_at - b.created_at;
  // Свои неотправленные (id < 0) в пределах секунды — после серверных.
  const al = a.local ? 1 : 0;
  const bl = b.local ? 1 : 0;
  if (al !== bl) return al - bl;
  return a.id - b.id;
}

const sorted = (list: UiMessage[]): UiMessage[] => [...list].sort(cmp);

/** Встроить сообщение сервера: заменить оптимистичное, обновить существующее или вставить. */
export function upsertServerMessage(state: MessagesState, incoming: CwMessage): MessagesState {
  const list = state.messages;

  const byEcho = incoming.echo_id ? list.findIndex((m) => m.local && m.echo_id === incoming.echo_id) : -1;
  if (byEcho >= 0) {
    const next = list.slice();
    next[byEcho] = { ...incoming };
    return { ...state, messages: sorted(next) };
  }

  const byId = list.findIndex((m) => !m.local && m.id === incoming.id);
  if (byId >= 0) {
    const next = list.slice();
    // content_attributes/attachments/status меняются после создания (расшифровка голоса, доставка).
    next[byId] = { ...list[byId], ...incoming, attachments: incoming.attachments ?? list[byId].attachments };
    return { ...state, messages: next };
  }

  return { ...state, messages: sorted([...list, incoming]) };
}

export interface LocalMessageInit {
  echoId: string;
  conversationId: number;
  content: string;
  isPrivate: boolean;
  userId: number | null;
  userName: string;
  /** Секунды Unix; параметром — чтобы проверять без часов. */
  now: number;
  /** Файлы, которые уходят: пузырь показывает их сразу (по ссылке из памяти телефона). */
  attachments?: CwAttachment[];
}

let localCounter = 0;

export function makeLocalMessage(init: LocalMessageInit): UiMessage {
  localCounter += 1;
  return {
    id: -localCounter,
    content: init.content,
    conversation_id: init.conversationId,
    message_type: MESSAGE_OUTGOING,
    created_at: init.now,
    private: init.isPrivate,
    echo_id: init.echoId,
    ...(init.attachments?.length ? { attachments: init.attachments } : {}),
    sender: init.userId !== null ? { id: init.userId, type: 'user', name: init.userName } : null,
    local: 'sending',
  };
}

export function addLocal(state: MessagesState, local: UiMessage): MessagesState {
  if (state.messages.some((m) => m.local && m.echo_id === local.echo_id)) return state;
  return { ...state, messages: sorted([...state.messages, local]) };
}

export function markLocal(
  state: MessagesState,
  echoId: string,
  patch: { local: 'sending' | 'failed'; localError?: string },
): MessagesState {
  return {
    ...state,
    messages: state.messages.map((m) =>
      m.local && m.echo_id === echoId ? { ...m, local: patch.local, localError: patch.localError } : m),
  };
}

export function removeLocal(state: MessagesState, echoId: string): MessagesState {
  return { ...state, messages: state.messages.filter((m) => !(m.local && m.echo_id === echoId)) };
}

const maxServerId = (list: UiMessage[]): number =>
  list.reduce((acc, m) => (m.local ? acc : Math.max(acc, m.id)), 0);

/**
 * Свежая страница (последние 20) поверх уже загруженного.
 * Нет пересечения и страница полная — значит, пока нас не было, пришло больше 20 сообщений:
 * старую часть бросаем (иначе в ленте дыра), «есть старше» включаем.
 */
export function mergeLatest(existing: MessagesState | undefined, fetched: CwMessage[]): MessagesState {
  const full = fetched.length >= MESSAGES_PAGE;
  if (!existing || existing.messages.length === 0) {
    return { messages: sorted(fetched), hasMore: full };
  }
  if (fetched.length === 0) return existing;

  const locals = existing.messages.filter((m) => m.local);
  const servers = existing.messages.filter((m) => !m.local);
  const oldestFetched = Math.min(...fetched.map((m) => m.id));

  const gap = full && oldestFetched > maxServerId(existing.messages);
  const base = gap ? [] : servers;
  const byId = new Map<number, UiMessage>(base.map((m) => [m.id, m]));
  fetched.forEach((m) => byId.set(m.id, { ...byId.get(m.id), ...m }));

  // Оптимистичное, которое сервер уже вернул (по echo_id), больше не нужно.
  const echoes = new Set(fetched.map((m) => m.echo_id).filter(Boolean));
  const keptLocals = locals.filter((m) => !(m.echo_id && echoes.has(m.echo_id)));

  return {
    messages: sorted([...byId.values(), ...keptLocals]),
    hasMore: gap ? true : existing.hasMore,
  };
}

/** Более старая страница (до самого раннего загруженного). */
export function prependOlder(state: MessagesState, older: CwMessage[]): MessagesState {
  const have = new Set(state.messages.filter((m) => !m.local).map((m) => m.id));
  const fresh = older.filter((m) => !have.has(m.id));
  return {
    messages: sorted([...fresh, ...state.messages]),
    hasMore: older.length >= MESSAGES_PAGE,
  };
}

/** id самого раннего серверного сообщения — якорь для before=. */
export function oldestServerId(state: MessagesState): number | null {
  const ids = state.messages.filter((m) => !m.local).map((m) => m.id);
  return ids.length ? Math.min(...ids) : null;
}
